import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SMLRouteNames } from '../../../routing/sml/SMLRoutes';
import {
  useShowSMLPricingModal,
  useSMLUpgradePrompt,
  useTourActive,
  useUserTokenDecoded,
} from '../../../redux/slices/appInfo/hooks';

const HOME_PATH = `/${SMLRouteNames.home}`;

/** True on the artist Home (`/app/home`). The router base `/app` is not part of the path. */
export function isHomePath(pathname: string): boolean {
  return pathname.replace(/\/+$/, '') === HOME_PATH;
}

/** Bootstrap (reactstrap `Modal`) puts `modal-open` on the body while a dialog shows. */
const isDialogOpen = (): boolean => typeof document !== 'undefined' && document.body.classList.contains('modal-open');

/** Follows the `modal-open` class of the body. */
function useDialogOpen(): boolean {
  const [open, setOpen] = useState(isDialogOpen);
  useEffect(() => {
    if (typeof MutationObserver === 'undefined') {
      return undefined;
    }
    const observer = new MutationObserver(() => setOpen(isDialogOpen()));
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return open;
}

/**
 * A quiet moment for the What's new panel (spec 2026-10-20 What's new §7): a signed-in person on
 * Home, with no dialog, no upgrade or pricing prompt and no LaziiBee tour in front.
 */
export function useQuietHome(): boolean {
  const { pathname } = useLocation();
  const signedIn = useUserTokenDecoded() !== null;
  const pricing = useShowSMLPricingModal();
  const upgrade = useSMLUpgradePrompt();
  const tour = useTourActive();
  const dialog = useDialogOpen();
  return signedIn && isHomePath(pathname) && !pricing && upgrade === null && !tour && !dialog;
}
